<template>
  <!-- StwButton -->
  <Button
    v-bind="$attrs"
    :size="primeSize"
    :rounded="rounded"
    :style="customStyle"
    :pt="{
      root: baseRootClass,
      label: 'font-medium',
    }"
  >
    <slot />
  </Button>
</template>

<script setup lang="ts">
import { Button } from 'primevue';
import { computed } from 'vue';

interface Props {
  size?: 'xs' | 'sm' | 'md' | 'lg';
  rounded?: boolean;
  // 'default' for neutral theme; 'primary' for the CityU brand gradient
  // (main actions); a CSS color value for other background overrides
  color?: string;
  colorClasses?: string; // utility class string override for full button styling
}

const props = withDefaults(defineProps<Props>(), {
  size: 'sm',
  rounded: true,
  color: 'default',
  colorClasses: '',
});

const primeSize = computed(() => {
  if (props.size === 'lg') return 'large';
  if (props.size === 'xs') return 'small';
  // default: make small to achieve compact UI
  return 'small';
});

const baseRootClass = computed(() => {
  // Simplified sizing - all buttons use consistent min-height
  const sizeClass = 'px-3 py-1.5 text-sm min-h-[2.5rem]';
  const radiusClass = props.rounded ? 'rounded-xl' : 'rounded-md';

  const sharedStyle = [
    'duration-0',
    'flex items-center justify-center gap-2',
    '!border-0',
  ].join(' ');

  const hasCustomColor = Boolean(props.color && props.color !== 'default');
  const colorClasses = props.colorClasses
    ? props.colorClasses
    : props.color === 'primary'
      ? 'stw-brand-gradient hover:brightness-110 focus:brightness-110'
      : hasCustomColor
        ? 'hover:brightness-90 focus:brightness-90'
        : '!bg-surface-300 !text-surface-950 hover:!bg-surface-400 focus:!bg-surface-400 focus:!ring-2 focus:![--tw-ring-color:var(--stw-glass-ring-focus)]';
  const disabledClasses =
    props.colorClasses || hasCustomColor
      ? 'disabled:opacity-60 disabled:cursor-not-allowed'
      : 'disabled:!bg-surface-100/40 disabled:!text-surface-400';

  return [
    sizeClass,
    radiusClass,
    sharedStyle,
    colorClasses,
    disabledClasses,
  ].join(' ');
});

// Picks white or near-black text for a hex background color, based on
// which gives the higher contrast ratio (WCAG relative luminance).
// Non-hex colors (e.g. a var() reference) fall back to white, matching
// the previous always-white behavior.
const contrastTextColor = (hex: string): string => {
  const match = /^#([0-9a-f]{6})$/i.exec(hex.trim());
  if (!match) return 'white';
  const [r, g, b] = [0, 2, 4].map(
    (i) => parseInt(match[1].slice(i, i + 2), 16) / 255
  );
  const toLinear = (c: number) =>
    c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  const luminance =
    0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
  const contrastWithWhite = 1.05 / (luminance + 0.05);
  const contrastWithBlack = (luminance + 0.05) / 0.05;
  return contrastWithWhite >= contrastWithBlack ? 'white' : '#121316';
};

const customStyle = computed(() => {
  if (
    props.color &&
    props.color !== 'default' &&
    props.color !== 'primary' &&
    !props.colorClasses
  ) {
    return {
      backgroundColor: props.color,
      color: contrastTextColor(props.color),
    } as const;
  }
  return undefined;
});
</script>

<style scoped></style>
